import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, Alert, Container } from '@mui/material';
import { AxiosResponse } from 'axios';
import { CreateBookingSlot } from '../types/bookingSlot';
import { BarberServices } from '../types/barber';
import { Booking } from '../types/booking';
import { Shop, ShopBarber } from '../types/shop';
import {
  getShopSlotsById,
  getShopSlotsByShopIdAndBarberId,
} from '../middleware/reservation_service/getRequests';
import {
  getShopBarberById,
  getShopOwnerById,
} from '../middleware/registration_service/getRequests';
import { createBooking } from '../middleware/reservation_service/createRequest';
import PublicBookingCalendar, {
  BookingFormData,
} from './components/PublicBookingCalendar';

const ShopBookingCalendar: React.FC = () => {
  const { shopId: shopIdParam } = useParams<{ shopId: string }>();
  const [searchParams] = useSearchParams();
  const parseBigIntParam = (value: string | null | undefined) => {
    if (!value) return null;
    try {
      return BigInt(value);
    } catch {
      return null;
    }
  };
  const shopId = parseBigIntParam(shopIdParam);

  // Present when the link was shared by an individual shop barber.
  const shopBarberIdParam = searchParams.get('barberId');
  const shopBarberId = parseBigIntParam(shopBarberIdParam);

  // Present when the link was shared by the shop owner.
  const ownerIdParam = searchParams.get('ownerId');
  const ownerId = parseBigIntParam(ownerIdParam);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shopName, setShopName] = useState('');
  const [barberName, setBarberName] = useState('');
  const [barbersById, setBarbersById] = useState<Map<string, ShopBarber>>(
    new Map()
  );
  const [slots, setSlots] = useState<CreateBookingSlot[]>([]);

  const loadShopBarberData = useCallback(async () => {
    if (!shopBarberId) return;

    try {
      const response = await getShopBarberById(shopBarberId);
      if (response.data) {
        const barber: ShopBarber = response.data;
        setBarbersById(new Map([[shopBarberId.toString(), barber]]));
        setBarberName(
          `${response.data.firstName || ''} ${
            response.data.lastName || ''
          }`.trim()
        );
      }
    } catch (err) {
      console.error('Failed to load shop barber data:', err);
      setError('Unable to load barber details. Please try again later.');
    }
  }, [shopBarberId]);

  const loadShopOwnerData = useCallback(async () => {
    if (!ownerId) return;

    try {
      const response = await getShopOwnerById(ownerId);
      const shop: Shop | undefined = response.data?.shops?.find(
        (s: Shop) => shopId !== null && BigInt(s.id || 0) === shopId
      );

      if (shop) {
        setShopName(shop.name || '');
        const map = new Map<string, ShopBarber>();
        (shop.shopBarbers || []).forEach((barber) => {
          if (barber.id !== undefined) {
            map.set(barber.id.toString(), barber);
          }
        });
        setBarbersById(map);
      }
    } catch (err) {
      console.error('Failed to load shop owner data:', err);
      setError('Unable to load shop details. Please try again later.');
    }
  }, [ownerId, shopId]);

  const loadSlots = useCallback(async () => {
    if (!shopId) return;

    try {
      setLoading(true);
      const response: AxiosResponse = shopBarberId
        ? await getShopSlotsByShopIdAndBarberId(shopId, shopBarberId)
        : await getShopSlotsById(shopId);

      const rawSlots = Array.isArray(response.data) ? response.data : [];

      const availableSlots: CreateBookingSlot[] = rawSlots
        .filter((slot: any) => slot.status === 'AVAILABLE')
        .map((slot: any) => ({
          id: slot.id,
          barber_id: BigInt(slot.barber_id),
          service_id: BigInt(slot.service_id),
          start_time: new Date(`${slot.slot_date}T${slot.start_time}:00`),
          status: slot.status,
          slot_date: new Date(slot.slot_date),
          shop_id: shopId,
        }));

      setSlots(availableSlots);
    } catch (err) {
      console.error('Failed to load shop slots:', err);
      setError('Unable to load available slots. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [shopId, shopBarberId]);

  useEffect(() => {
    if (!shopId) {
      setError('Invalid booking link.');
      return;
    }

    if (shopBarberId) {
      loadShopBarberData();
    } else {
      loadShopOwnerData();
    }
    loadSlots();
  }, [shopId, shopBarberId, loadShopBarberData, loadShopOwnerData, loadSlots]);

  const getBarberName = (barberId: bigint) => {
    const barber = barbersById.get(barberId.toString());
    return barber
      ? `${barber.firstName || ''} ${barber.lastName || ''}`.trim()
      : '';
  };

  const getBarberEmail = (barberId: bigint) => {
    const barber = barbersById.get(barberId.toString());
    return barber?.email;
  };

  const getService = (serviceId: bigint): BarberServices | undefined => {
    for (const barber of barbersById.values()) {
      const service = barber.barberShopServices?.find(
        (s) => BigInt(s.id || 0) === serviceId
      );
      if (service) return service;
    }
    return undefined;
  };

  const handleBook = async (
    slot: CreateBookingSlot,
    formData: BookingFormData
  ) => {
    const service = getService(slot.service_id);
    const barberName = getBarberName(slot.barber_id);
    const barberEmail = getBarberEmail(slot.barber_id);

    if (!service) {
      throw new Error('Unable to retrieve complete slot information.');
    }

    const reservationData: Booking = {
      slot_id: slot.id,
      customer_email: formData.customerEmail,
      customer_phone: formData.customerPhone,
      customer_name: formData.customerName,
      barber_name: barberName,
      barber_email_address: barberEmail,
      service_name: service.name,
      price: service.price,
      status: 'PENDING',
      type: 'SHOP',
      barber_id: slot.barber_id,
      shop_id: shopId ?? undefined,
    };

    await createBooking(reservationData);
    setSlots((prev) => prev.filter((s) => s.id !== slot.id));
  };

  if (!shopId) {
    return (
      <Container sx={{ marginTop: '75px !important' }}>
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Invalid booking link.</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <PublicBookingCalendar
      title={
        shopName
          ? `Book with ${shopName}`
          : barberName
          ? `Book with ${barberName}`
          : "Shop's Calendar"
      }
      subtitle="Select a date and time to create your reservation"
      loading={loading}
      error={error}
      slots={slots}
      showBarberName={!shopBarberId}
      getBarberName={getBarberName}
      getService={getService}
      onBook={handleBook}
    />
  );
};

export default ShopBookingCalendar;
